import { useMutation, useQuery } from '@apollo/client';
import type {
  GetComposedForm_composedForm,
  GetComposedFormVariables,
  LayoutElement as ILayoutElement,
  GetComposedForm_composedForm_questions as Question,
} from '@kizi/comparator-core';
import {
  ComparatorContext,
  ComponentDescriptor,
  ComponentRenderer,
  crawlLayoutElement,
  Expressions,
  FieldHandler,
  FormField,
  getCalculateSessionId,
  getComposedForm,
  getRendererFromDescriptor,
  jsonObjectBuilder,
  KiziLogger,
  LabelLayoutElement,
  orderHomeQueryApollo,
  parseComposedForm,
  QuestionLayoutElement,
  sanitizeName,
  SetFieldsEvent,
  useField,
  useFields,
  useTransformers,
  useValidators,
  useWizardService,
  validators as ValidatorsModule,
  WizardInterpreter,
  WizardState,
} from '@kizi/comparator-core';
import { Div, Label, Span } from '@kizi/core-components';
import _ from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getAnalyticsSessionIdCookieName,
  getCookie,
} from '../../../utils/global';
import { FormSubmit } from '../../Form';
import { Spinner } from '../../Spinner';
import { Wizard, WizardProps } from '../../Wizard';
import { GoToButton } from '../Button/GoToButton';
import { sendCustomEvent, Separator } from '../TabViewWizard/ApplyPageWizard';
import { getFormName } from './utils';

const parseJsonPath = (o, p) =>
  o ? p.split('.').reduce((r, k) => r[k], o) : undefined;

const findEmailValue = (steps, emailFieldName) => {
  const data = {};
  steps.forEach((step) => {
    step.fields.forEach((field) => {
      if (field.name === emailFieldName) {
        data['email'] = field.value;
      }
    });
  });
  return data;
};

export const getCbMembershipOnBlur =
  ({ emailFieldName, api, setIsMember, parentWizard, setIsLoading }) =>
  (state, e) => {
    if (
      e.hasOwnProperty('state') &&
      e.state.event.type === 'CHANGE' &&
      e.state.event.target.name === emailFieldName
    ) {
      parentWizard?.send({
        type: 'SET_GLOBAL',
        fields: [
          {
            name: 'global.isWaitingForCbApiResponse',
            value: 1,
          },
        ],
      });
    }
    if (
      e.hasOwnProperty('state') &&
      e.state.event.type === 'blur' &&
      e.state.event.target.name === emailFieldName
    ) {
      let data: object = findEmailValue(
        state.context.initialSteps,
        emailFieldName,
      );
      parentWizard?.send({
        type: 'SET_GLOBAL',
        fields: [
          {
            name: 'global.isWaitingForCbApiResponse',
            value: 1,
          },
        ],
      });
      setIsLoading(true);
      if (Object.getOwnPropertyDescriptor(data, 'email')) {
        SendCbMembershipEvent(
          data,
          api,
          setIsMember,
          parentWizard,
          setIsLoading,
        );
      }
    }
  };

export const SendCbMembershipEvent = (
  data,
  api,
  setIsMember,
  parentWizard,
  setIsLoading,
): void => {
  api
    .checkMembershipStatus(data.email)
    .then((exists) => {
      parentWizard?.send({
        type: 'SET_GLOBAL',
        fields: [
          {
            name: 'global.isMember',
            value: exists ? 1 : 0,
          },
          {
            name: 'global.isWaitingForCbApiResponse',
            value: 0,
          },
        ],
      });
      setIsMember(exists);
    })
    .catch((err) => {
      console.log(err);
      setIsMember(false);
      parentWizard?.send({
        type: 'SET_GLOBAL',
        fields: [
          {
            name: 'global.isMember',
            value: 0,
          },
          {
            name: 'global.isWaitingForCbApiResponse',
            value: 0,
          },
        ],
      });
    })
    .finally(() => {
      setIsLoading(false);
    });
};

type DynamicFormProps = {
  postOrderDataKey?: string;
  submitButtonText?: string;
  comparatorType?: string;
  postOrderUrl?: string;
  hasCustomWrapper?: boolean;
  requestVariables?: any;
  postQuery?: any;
  customBlur?: Function;
  selectCustomOnChange?: (e, q) => void;
  customCheckboxOnChange?: (e, q) => void;
  formData?: GetComposedForm_composedForm;
  selectedProductFieldName?: string;
  errorMessage?: React.ReactNode;
  wizardStepsKey: string;
  injectedFields?: { [nameInMainWizard: string]: string }; // ex: { 'globals.isMember' : 'cbmembership.isMember' }
  onCurrentTabChange?: (currentIndex?: number) => void;
  dependencyFieldSubmitButton?: string;
  dependencyConditionSubmitButton?: string;
  dependencyLabelSubmitButton?: string;
  postOrderIdPath?: string;
  constructOrderVariables?: (data) => any;
} & GetComposedFormVariables;

const useWizardCallback = (): [
  WizardProps['wizardCallback'],
  WizardInterpreter | undefined,
] => {
  const [wizardService, setWizardService] = useState<WizardInterpreter>();
  const callback = useCallback(
    (wizard) => {
      if (wizardService !== wizard) setWizardService(wizard);
    },
    [setWizardService],
  );
  return [callback, wizardService];
};

export const getParentListener =
  (
    parentWizard: WizardInterpreter | undefined,
    dynamicFormWizard: WizardInterpreter | undefined,
  ) =>
  (s: WizardState, e) => {
    if (e.type === 'SET_GLOBAL') {
      const steps = dynamicFormWizard?.getSnapshot()?.context?.steps;

      if (steps) {
        let stepIndex = 0;
        for (let i = 0; i < steps.length; i++) {
          const fieldIndex = steps[i]
            .getSnapshot()
            ?.context.fields.findIndex((e) => e.name.includes('userIsMember'));
          if (fieldIndex != -1) stepIndex = i;
        }

        steps[stepIndex].send({
          type: 'SET',
          fields: [
            {
              name: 'consumentenbond-userIsMember',
              value: e.fields.find(
                (f: FormField) => f.name === 'global.isMember',
              )?.value,
            },
            {
              name: 'currentlyChecking',
              value: e.fields.find(
                (f: FormField) => f.name === 'global.isWaitingForCbApiResponse',
              )?.value,
            },
          ],
        });
      }
    }

    if (
      e.type === 'xstate.update' &&
      e.state &&
      e.id &&
      e.state.matches('completed') &&
      e.id === s.context.initialSteps[s.context.currentStepIndex].name &&
      e?.state?.event?.fields?.[0]?.name === 'wizardSteps'
    ) {
      parentWizard?.send('GOTO', {
        index: s.context.currentStepIndex + 1,
      });
    }
  };

export const getSubmitListener =
  (
    parentWizard: WizardInterpreter | undefined,
    wizardStepsKey: string,
    selectedProduct,
    frontEndName: string,
    clientId: string,
    postOrder,
    requestVariables,
    postOrderDataKey,
    state,
    postOrderIdPath: string,
    constructOrderVariables,
  ) =>
  (s: WizardState, e) => {
    const split = wizardStepsKey.split('.');

    if (split.length > 1 && e.type === 'GOTO') {
      sendCustomEvent('wizard-apw-step-change', {
        step: s.context.currentStepIndex,
        selectedProduct: selectedProduct,
        ctx: s.context,
      });
    }

    if (e.type === 'NEXT' && _.last(s.context.steps)?.getSnapshot()?.done) {
      const selectedProductVal = JSON.parse(selectedProduct as string);
      const comparisonId =
        selectedProductVal?.commonTechnical[0].transactionReference;
      const productCode = selectedProductVal?.productCode;
      const sessionId = getCalculateSessionId();
      const [formName, fieldName] = split;

      let jsonObjectBuilderData = jsonObjectBuilder(s.context.initialSteps);

      const isCbLoggedIn =
        (parentWizard as any).state.context.globalFields[2]?.value === 1;
      let consumentenbondMemberEmail = '';

      if (isCbLoggedIn) {
        const b64 = localStorage.getItem('SCW_LOGIN_ACCESS_TOKEN');
        if (b64) {
          const back = atob(b64.split('.')[1]);
          consumentenbondMemberEmail = JSON.parse(back).sub;
        }
      }

      let builderData = {
        ...jsonObjectBuilderData,
        ...(isCbLoggedIn && {
          partyPolicyHolders: {
            ...jsonObjectBuilderData.partyPolicyHolders,
            customProperties: {
              ...jsonObjectBuilderData.partyPolicyHolders.customProperties,
              consumentenbondMemberEmail,
              consumentenbondMember: true,
            },
          },
        }),
      };

      let variables = {};

      if (constructOrderVariables) {
        variables = constructOrderVariables({
          sessionId,
          state,
          frontEndName,
          clientId,
          requestVariables,
          product: selectedProductVal,
          builderData,
        });
      } else {
        variables = {
          comparisonId: comparisonId,
          chosenProductKrn: productCode,
          frontEndName,
          sessionId,
          clientId: clientId,
          googleAnalyticsId: getCookie('_ga'),
          fbp: getCookie('_fbp'),
          fbc: getCookie('_fbc'),
          vwoId: getCookie('_vwo_uuid'),
          gclid: localStorage.getItem('gclid'),
          googleAnalyticsSessionId: getCookie(
            getAnalyticsSessionIdCookieName(),
          ),
          [postOrderDataKey]: {
            ...builderData,
            process: {
              reference: window.navigator.userAgent.includes('CBAPP')
                ? 'CBAPP'
                : (getCookie('ref') ?? ''),
            },
          },
        };
      }
      console.log(variables);

      postOrder({ variables })
        .then((result) => {
          let orderId = parseJsonPath(result, postOrderIdPath);

          if (orderId) {
            parentWizard?.send([
              {
                type: 'SET_FORM_FIELDS',
                name: formName,
                fields: [
                  {
                    name: 'orderId',
                    value: orderId,
                  },
                  { name: fieldName, value: s.context.initialSteps as any },
                ],
              },
            ]);
          } else {
            parentWizard?.send([
              {
                type: 'SET_FORM_FIELDS',
                name: formName,
                fields: [
                  {
                    name: 'orderId',
                    value: 'error',
                  },
                  { name: fieldName, value: s.context.initialSteps as any },
                ],
              },
            ]);
          }
        })
        .catch((err) => {
          KiziLogger.logError({
            eventId: 0,
            member: 'DynamicForm - getSubmitListener',
            message: 'Error in useEffect',
            eventName: 'PostOrder error',
            data: {
              error: (err as Error).message,
              variables,
            },
          });
          parentWizard?.send([
            {
              type: 'SET_FORM_FIELDS',
              name: formName,
              fields: [
                {
                  name: 'orderId',
                  value: 'error',
                },
                { name: fieldName, value: s.context.initialSteps as any },
              ],
            },
          ]);
        });
    }
  };

export const DynamicForm: React.FC<DynamicFormProps> = ({
  formName,
  submitButtonText = 'Bevestig je aanvraag',
  postOrderDataKey = 'package',
  postOrderUrl,
  frontEndName = '',
  clientName,
  errorMessage,
  wizardStepsKey,
  injectedFields,
  selectedProductFieldName = 'overview.selectedProduct',
  formData,
  hasCustomWrapper = false,
  requestVariables,
  customBlur,
  selectCustomOnChange,
  customCheckboxOnChange,
  postQuery = orderHomeQueryApollo,
  onCurrentTabChange,
  dependencyFieldSubmitButton,
  dependencyConditionSubmitButton,
  dependencyLabelSubmitButton,
  postOrderIdPath = 'data.orderHomeContentPackage.orderId',
  constructOrderVariables,
}) => {
  const validatedField = useField('validatedField');
  const [selectedProductField, ...injectedFieldHandlers] = useFields([
    selectedProductFieldName,
    ...(injectedFields ? Object.keys(injectedFields) : []),
  ]);

  const { api, getStateFromLocalStorage } = useContext(ComparatorContext);
  const persistedState = getStateFromLocalStorage();
  const clientId = api.credentials.clientId || '';
  const [isMember, setIsMember] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const selectedProduct = selectedProductField?.value as string;
  const orderFormId = JSON.parse(selectedProduct).orderFormId;

  let dynamicFormUrl = `${process.env.KIZI_PUBLIC_DOMAIN}/dynamicforms/graphql`;

  const { error, data } = useQuery(getComposedForm, {
    context: {
      ...(dynamicFormUrl !== '' ? { uri: dynamicFormUrl } : {}),
      headers: {
        'authorization':
          api.credentials.token || localStorage.getItem('accessToken'),
      },
    },
    variables: {
      formName: orderFormId || formName,
      frontEndName,
      clientName,
    },
    skip: hasCustomWrapper,
    fetchPolicy: 'no-cache',
  });

  let stepsFromRequest;

  const [postOrder, { error: postError }] = useMutation(postQuery, {
    context: {
      ...(postOrderUrl !== '' ? { uri: postOrderUrl } : {}),
      headers: {
        'authorization':
          api.credentials.token || localStorage.getItem('accessToken'),
      },
    },
  });
  const validators = useValidators() ?? {};
  const transformers = useTransformers() ?? {};
  const [wizardCallback, wizardService] = useWizardCallback();
  const parentWizard = useWizardService();

  // Email field is making a request, thus re-rendering dynamicForm
  // So layoutEffect gets called again, and filling the fields with errors. Thats why we pass it as dependacy to setFields effect
  const consumentenbondMemberEmailField = wizardService
    ?.getSnapshot()
    .context.initialSteps.flatMap((s) => s.fields)
    .find((field) => field.name === 'consumentenbond-memberEmail');

  useEffect(() => {
    if (parentWizard) {
      const overviewPageIndex =
        parentWizard.state.context.initialSteps.findIndex(
          (e) => e.name === 'overview',
        );
      setTimeout(() => {
        parentWizard?.send({
          type: 'GOTO',
          index: overviewPageIndex,
        });
      }, 86400000); // auto refresh after 24 hours
    }
  }, [parentWizard]);

  useEffect(() => {
    if (wizardService && hasCustomWrapper) {
      if (formData && Array.isArray(formData.questions)) {
        let fieldsToUpdateFromRequest = formData.questions.filter(
          (question) => {
            return (
              question?.name === validatedField?.value ||
              (question?.valueOptions && question?.valueOptions?.length > 0)
            );
          },
        );

        const wizardSteps = wizardService
          .getSnapshot()
          .context.initialSteps.flatMap((step) => step.fields);

        if (formData.questions.length !== Number(sessionStorage.getItem("formDataLength"))) {
          formData.questions.forEach((questionElement) => {
            const index = wizardSteps.findIndex((wizardElement) => {
              return (
                wizardElement.name === questionElement.name.replaceAll('.', '-')
              );
            });
            sessionStorage.setItem("formDataLength", String(formData.questions.length))
            if (index === -1) {
              fieldsToUpdateFromRequest = [
                ...fieldsToUpdateFromRequest,
                questionElement,
              ];
            }
          });
        }
        fieldsToUpdateFromRequest = [
          ...fieldsToUpdateFromRequest,
          ...formData.questions.filter(
            (question) =>
              question?.name === 'zorgweb.zorgvragenMap.eindverklaring',
          ),
        ];
        for (const field of fieldsToUpdateFromRequest) {
          let validators: any[] = [];
          if (Array.isArray(field.validators)) {
            validators = field.validators.map((val) => ({
              fn: val.name,
              args: val?.pattern
                ? [val?.message, val?.pattern, 'i']
                : [val?.message],
            }));
          }

          const options = field?.valueOptions?.map((option) => ({
            label: option.display,
            value: option.value,
          }));

          const errors = field?.validators
            ?.map((val) =>
              ValidatorsModule?.[val.name](val?.message)(field.defaultValue),
            )
            .filter(Boolean) as [];

          const formName = getFormName(stepsFromRequest, field);

          // Create event generator with comprehensive field data
          const event = (
            type: 'ADD_FIELDS' | 'SET_FIELDS',
            customProps = {},
          ) => ({
            type,
            fields: [
              {
                ...field,
                ...(!field.name.includes('consumentenbond') && {
                  formName,
                }),
                value: field?.defaultValue || field?.value,
                name: field?.name?.replaceAll('.', '-'),
                validator: validators,
                options,
                errors:
                  errors?.length > 0 &&
                  field?.defaultValue &&
                  field?.defaultValue !== 'false'
                    ? errors
                    : undefined,
                ...customProps,
              },
            ],
          });

          // If NEW field is coming from later requests, it does NOT exist on wizard fields, so we have to add it
          const wizardSteps = wizardService
            .getSnapshot()
            .context.initialSteps.flatMap((step) => step.fields);
          const fieldName = field.name?.replaceAll('.', '-');
          const fieldOnWizard = wizardSteps.find((f) => {
            return f.name === fieldName;
          });

          if (fieldOnWizard?.name !== fieldName) {
            wizardService?.send(event('ADD_FIELDS'));

            // Also setting the field because it seems to happen async
            wizardService?.send(
              event('SET_FIELDS', {
                meta: {
                  originalQuestion: field,
                },
              }),
            );
          }
          wizardService?.send(event('SET_FIELDS'));
        }
      }
    }
  }, [
    JSON.stringify(formData),
    JSON.stringify(consumentenbondMemberEmailField),
    validatedField?.value,
  ]);

  useEffect(() => {
    const parentListener = getParentListener(parentWizard, wizardService);
    parentWizard?.onTransition(parentListener);
    const onSubmitListener = getSubmitListener(
      parentWizard,
      wizardStepsKey,
      selectedProduct,
      frontEndName,
      clientId,
      postOrder,
      requestVariables,
      postOrderDataKey,
      persistedState,
      postOrderIdPath,
      constructOrderVariables,
    );
    wizardService?.onTransition(onSubmitListener);
    return () => {
      wizardService?.off(onSubmitListener);
      parentWizard?.off(parentListener);
    };
  }, [wizardService, parentWizard]);

  useEffect(() => {
    const cbMembershipOnBlur = getCbMembershipOnBlur({
      emailFieldName: 'consumentenbond-memberEmail',
      api,
      setIsMember,
      parentWizard,
      setIsLoading,
    });
    wizardService?.onTransition(cbMembershipOnBlur);
    return () => {
      wizardService?.off(cbMembershipOnBlur);
    };
  }, [wizardService]);

  useEffect(() => {
    const onWaitingApiResponseChange = (prevCtx, currentCtx) => {
      const prevIsWaiting = prevCtx?.globalFields?.find(
        (f) => f.name === 'global.isWaitingForCbApiResponse',
      )?.value;
      const currentIsWaiting = currentCtx?.globalFields?.find(
        (f) => f.name === 'global.isWaitingForCbApiResponse',
      )?.value;
      const steps = currentCtx?.initialSteps;

      if (prevIsWaiting !== currentIsWaiting && steps) {
        wizardService?.send({
          type: 'SET_FIELDS',
          fields: [{ name: '', formName: steps[0].name }],
        });
      }
    };
    parentWizard?.onChange(onWaitingApiResponseChange);
    return () => {
      parentWizard?.off(onWaitingApiResponseChange);
    };
  }, [parentWizard, wizardService]);

  if (error) return <div>{errorMessage ?? 'error: ' + error.message}</div>;
  if (postError)
    return <div>{errorMessage ?? 'error: ' + postError.message}</div>;

  if (!data && !hasCustomWrapper) return null;

  const { steps, layout } = parseComposedForm(
    hasCustomWrapper ? formData : data.fullComposedForm,
    customBlur,
    selectCustomOnChange,
    customCheckboxOnChange,
    JSON.parse(selectedProduct),
  );

  stepsFromRequest = steps;
  const stepsWithExternalValues = steps.map((s) => {
    const updatedFields = s.fields.map((f) => {
      const handlerIndex = Object.values(injectedFields ?? {}).findIndex(
        (mappedName) => f.name === mappedName,
      );
      if (handlerIndex === -1) return f;
      return {
        ...f,
        // houseNumberAddition is coming with valueType:string, so it adds a validator of isString. Tell BE to change this field to valueType of null
        ...(f.name === 'houseNumberAddition' && { validator: [] }),
        value: injectedFieldHandlers[handlerIndex]?.value,
      };
    });
    return { ...s, fields: updatedFields };
  });

  const jsxLayout = layout.map(({ components }, i) => [
    <Div
      key={`d-form-field-${i}`}
      display="flex"
      flexDirection="row"
      alignItems="center"
      variant="lightTabViewTabsContainer"
      justifyContent={['center', 'left']}
    >
      {steps.map((s, index) => {
        return (
          <Div key={s.name} style={{ display: 'flex' }}>
            <GoToButton index={index} variant="applyPageNavigation">
              {i === index ? s.name : s.name[0]}
            </GoToButton>
            {index < steps.length - 1 ? (
              <Separator key={'separator-' + index} color="supplementary.6" />
            ) : (
              <Div key={'separator-' + index} />
            )}
          </Div>
        );
      })}
    </Div>,
    ...(getRendererFromDescriptor(components) as Array<ComponentDescriptor>),
    <SubmitButton
      key={`dynamic-form-step-${i}`}
      isLast={i === layout.length - 1}
      wizard={parentWizard}
      isLoading={isLoading}
      submitButtonText={submitButtonText}
      dependencyField={dependencyFieldSubmitButton}
      dependencyCondition={dependencyConditionSubmitButton}
      dependencyLabel={dependencyLabelSubmitButton}
      isMember={isMember}
    />,
  ]);
  return (
    <>
      <Wizard
        startIndex={0}
        steps={stepsWithExternalValues}
        layout={jsxLayout}
        validators={validators}
        transformers={transformers}
        wizardCallback={wizardCallback}
        onRender={onCurrentTabChange}
      />
    </>
  );
};

export type LayoutElementProps = {
  originalDescriptor: ILayoutElement;
  originalQuestion?: Question;
  componentDescriptor: ComponentDescriptor;
  onCurrentTabChange?: (currentIndex: number) => void;
};

const isVisibleFrom = (
  {
    currentField,
    referencedFields,
  }: {
    currentField: FieldHandler | undefined;
    referencedFields: (FieldHandler | undefined)[];
  },
  visibleExpression: Expressions.Descriptor | undefined,
) => {
  try {
    return visibleExpression
      ? Expressions.isTrue(
          Expressions.parse(visibleExpression)(
            currentField?.value,
            referencedFields.filter((f): f is FieldHandler => !!f),
          ),
        )
      : true;
  } catch (e) {
    // console.warn(message, visibleExpression);
    return false;
  }
};
const useTextExpression = (
  originalDescriptor,
  componentDescriptor,
  references,
) => {
  return useMemo(() => {
    const textPropMap = componentDescriptor?.meta?.propsMap?.text;
    if (!originalDescriptor.textExpression || !textPropMap)
      return componentDescriptor;
    const textValue = Expressions.parse(originalDescriptor.textExpression)(
      '',
      references,
    );
    return {
      ...componentDescriptor,
      props: {
        ...componentDescriptor.props,
        [textPropMap]: textValue,
      },
    };
  }, [originalDescriptor, componentDescriptor, references]);
};

const isQuestionEditable = (
  originalQuestion,
  currentField,
  referencedFields,
) => {
  try {
    const parsedExpression = Expressions.parse(
      originalQuestion.editableExpression,
    );
    const evaluatedResult = parsedExpression(
      currentField?.value,
      referencedFields.filter((f): f is FieldHandler => !!f),
    );

    return Expressions.isTrue(evaluatedResult);
  } catch (error) {
    KiziLogger.logError({
      eventId: 0,
      member: 'Wizard',
      message: 'Error in isQuestionEditable',
      eventName: 'ExpressionEvaluationError',
      data: {
        error,
        editableExpression: originalQuestion.editableExpression,
      },
    });

    return false;
  }
};

const useEditableExpression = (
  originalQuestion,
  componentDescriptor,
  { currentField, referencedFields },
) =>
  useMemo(() => {
    const isNotEditable =
      originalQuestion !== undefined &&
      originalQuestion.editableExpression !== undefined &&
      !isQuestionEditable(originalQuestion, currentField, referencedFields);

    const displayValue = currentField?.options
      ? currentField.options.find((opt) => opt.value === currentField.value)
          ?.label
      : currentField?.value;

    // Enhance componentDescriptor with validators and valueType from originalQuestion
    const enhancedComponentDescriptor = useMemo(() => {
      if (!componentDescriptor || !originalQuestion) return componentDescriptor;

      return {
        ...componentDescriptor,
        props: {
          ...componentDescriptor.props,
          component: {
            ...componentDescriptor.props.component,
            props: {
              ...componentDescriptor.props.component.props,
              validators: originalQuestion.validators,
              valueType: originalQuestion.valueType,
              meta: {
                originalQuestion: originalQuestion,
              },
            },
          },
        },
      };
    }, [componentDescriptor, originalQuestion]);

    return isNotEditable ? (
      <Div
        display="flex"
        flexDirection="column"
        my={3}
        {...componentDescriptor?.props?.component?.props}
      >
        <Label>{currentField?.label || originalQuestion?.label}</Label>
        <Span>{displayValue || originalQuestion?.defaultValue}</Span>
      </Div>
    ) : (
      <ComponentRenderer serializedComponent={enhancedComponentDescriptor} />
    );
  }, [originalQuestion, componentDescriptor, currentField, referencedFields]);

const useReferences = (
  ...expressions: (Expressions.Descriptor | undefined)[]
) => {
  return useMemo(() => {
    return expressions
      .map((expr) => {
        return Expressions.getAllReferences(
          expr ?? ({} as Expressions.Descriptor),
          'name',
        );
      })
      .flat();
  }, expressions);
};

export const LayoutElement: React.FC<LayoutElementProps> = ({
  originalDescriptor,
  originalQuestion,
  componentDescriptor,
  onCurrentTabChange,
}) => {
  const references = useReferences(
    originalQuestion?.visibleExpression,
    originalDescriptor.visibleExpression,
    originalQuestion?.editableExpression,
    (originalDescriptor as LabelLayoutElement)?.textExpression,
  );

  const [currentField, ...referencedFields] = useFields([
    sanitizeName(originalQuestion?.name ?? ''),
    ...references.map((r) => sanitizeName(r)),
  ]);

  const wizard = useWizardService();

  useEffect(() => {
    const currentIndex = wizard?.getSnapshot()?.context?.currentStepIndex;
    if (
      currentIndex !== undefined &&
      typeof onCurrentTabChange === 'function'
    ) {
      onCurrentTabChange(currentIndex);
    }
  }, [wizard?.getSnapshot()?.context?.currentStepIndex]);

  const isVisible = useMemo(() => {
    const isVisibleFromQuestion = isVisibleFrom(
      { currentField, referencedFields },
      originalQuestion?.visibleExpression,
    );
    const isVisibleFromElement = isVisibleFrom(
      { currentField, referencedFields },
      originalDescriptor.visibleExpression,
    );
    return isVisibleFromQuestion && isVisibleFromElement;
  }, [
    currentField,
    referencedFields,
    originalQuestion?.visibleExpression,
    originalDescriptor.visibleExpression,
  ]);

  useEffect(() => {
    const subElements = crawlLayoutElement(originalDescriptor, (elmt) =>
      elmt.hasOwnProperty('name'),
    );

    const fields: FormField[] = [];

    if (currentField) {
      const { onBlur: _ob, onChange: _oc, ...otherFieldProps } = currentField;
      fields.push({
        ...otherFieldProps,
        active: isVisible,
        errors: undefined,
        options: currentField.options,
      });
    }

    subElements.forEach((el) => {
      let field: FormField | undefined;
      for (const step of wizard?.getSnapshot().context.initialSteps ?? []) {
        field = step.fields.find(
          (f) => f.name === sanitizeName((el as QuestionLayoutElement).name),
        );

        if (field) {
          fields.push({
            ...field,
            formName: step.name,
            active: isVisible,
            errors: undefined,
            options: field.options,
          });
          break;
        }
      }
    });

    if (fields.length > 0) {
      const event: SetFieldsEvent = {
        type: 'SET_FIELDS',
        fields: fields.map((f) => ({
          ...f,
          active: isVisible,
          errors: undefined,
          options: f.options,
        })),
      };
      wizard?.send(event);
    }
  }, [isVisible, currentField?.value]);

  const updatedDescriptor = useTextExpression(
    originalDescriptor,
    componentDescriptor,
    referencedFields,
  );

  const renderedComponent = useEditableExpression(
    originalQuestion,
    updatedDescriptor,
    { currentField, referencedFields },
  );
  return isVisible ? renderedComponent : null;
};

const SubmitButton: React.FC<{
  isLast: boolean;
  wizard?: WizardInterpreter;
  isLoading: boolean;
  submitButtonText: string;
  comparatorType?: string;
  dependencyField?: string;
  dependencyCondition?: string;
  dependencyLabel?: string;
  isMember?: boolean;
}> = ({
  isLast,
  wizard,
  isLoading,
  submitButtonText,
  dependencyField,
  dependencyCondition,
  dependencyLabel,
  isMember,
}) => {
  const lastStepField = useField('apply.lastStep', wizard);
  lastStepField?.set({ value: `${isLast}` });

  const wizardService = useWizardService();
  const memberOptInField = useField('consumentenbond-member', wizardService);

  const [label, setLabel] = useState(
    !isLast ? 'Volgende stap' : submitButtonText,
  );

  const depField = useField(dependencyField || '');

  useEffect(() => {
    if (depField && depField?.value) {
      if (!isLast) {
        setLabel('Volgende stap');
      } else if (
        isLast &&
        depField?.value === dependencyCondition &&
        dependencyLabel
      ) {
        setLabel(dependencyLabel);
      } else {
        setLabel(submitButtonText);
      }
    }
  }, [
    depField,
    dependencyCondition,
    submitButtonText,
    dependencyLabel,
    isLast,
  ]);

  return (
    <FormSubmit
      display="flex"
      alignItems="center"
      justifyContent="center"
      variant="primary"
      disabled={
        isLoading || (isMember === false && memberOptInField?.value !== 'false')
      }
      isLast={isLast}
    >
      {isLoading ? (
        <Div display="flex">
          <Spinner margin="auto" />
          <Div marginLeft={1}>E-mail valideren...</Div>
        </Div>
      ) : (
        <Div>{label}</Div>
      )}
    </FormSubmit>
  );
};
